import React from "react";
import {
    Modal,
    Button,
    Container,
    Row,
    Col,
    ListGroup,
    ListGroupItem,
    Alert,
} from "react-bootstrap";
import ReactDOMServer from "react-dom/server";
import SignatureHTML from "./SignatureHTML";
import { ISignatureState } from "./Models";
import { FaExternalLinkAlt } from "react-icons/fa";
import WorkingButton from "../core/WorkingButton";
import { isMobile } from "react-device-detect";
import AlertMessage from "../core/Alert";

interface ISignatureDialogProps {}

interface ISignatureDialogState {
    show: boolean;
    isCopying: boolean;
    isCoppied: boolean;
    type: string | undefined;
    isMobile: boolean;
    signatureProps?: ISignatureState;
}

class SignatureDialog extends React.Component<
    ISignatureDialogProps,
    ISignatureDialogState
> {
    private _signatureProps: ISignatureState | null;
    private _buttonText: string = "Copy to clipboard";
    private _copyType: "html" | "rich" | undefined;

    constructor(props: ISignatureDialogProps) {
        super(props);
        this._signatureProps = null;

        this._copyType = undefined;

        this.state = {
            show: false,
            isCopying: false,
            isCoppied: false,
            type: undefined,
            isMobile: isMobile,
            signatureProps: undefined,
        };

        this.copyRichText = this.copyRichText.bind(this);
        this.copyHTML = this.copyHTML.bind(this);
    }

    public open(signatureProps: ISignatureState): void {
        this.setState({ signatureProps: signatureProps });
    }

    public get show(): boolean {
        return this.state.show;
    }

    public set show(value: boolean) {
        this.setState({ show: value });
    }

    handleClose = () => {
        this.show = false;
    };

    copyHTML(evt: React.MouseEvent) {
        if (!this.state.isCopying) {
            this.startCopy("html");
            const element = document.getElementById(
                "htmlDiv"
            ) as HTMLDivElement;

            var range: Range = document.createRange();
            range.selectNodeContents(element as Node);

            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);

            document.execCommand("copy");

            window.getSelection()?.removeAllRanges();
        }
    }

    copyRichText(evt: React.MouseEvent) {
        if (!this.state.isCopying) {
            this.startCopy("rich");
            const element = document.getElementById(
                "outlookDiv"
            ) as HTMLDivElement;

            var range: Range = document.createRange();
            range.selectNodeContents(element as Node);

            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);

            document.execCommand("copy");

            window.getSelection()?.removeAllRanges();
        }
    }

    startCopy = (type: string) => {
        this.setState({ type: type, isCopying: true, isCoppied: false });
    };

    stopCopy = () => {
        this.setState({ isCopying: false, isCoppied: true });
    };

    getCopyCompleteHtml = () => {
        return (
            <React.Fragment>
                <b>Signature Copied. Next:</b>
                <ol className="completedList">
                    <li>Open your email application</li>
                    <li>locate "Signature" under settings</li>
                    <li>right click and select paste into the editor</li>
                </ol>
            </React.Fragment>
        );
    };

    handleCompleteClose = () => {
        this.setState({ type: undefined });
    };

    public showDialog(signatureProps: ISignatureState): void {
        this.setState({
            signatureProps: signatureProps,
            show: true,
        });
    }

    render() {
        return (
            <Modal show={this.state.show} onHide={this.handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Choose Format</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container style={{ fontSize: "80%", marginTop: 10 }}>
                        <Row>
                            <Col sm={12} md={6} lg={6}>
                                <AlertMessage
                                    show={this.state.isMobile}
                                    variant="danger"
                                    dismissible={true}
                                    style={{ marginTop: 10 }}
                                    onClick={() => {
                                        this.setState({ isMobile: false });
                                    }}
                                >
                                    Mobile Outlook app does not support HTML or
                                    Rich Text.
                                </AlertMessage>
                                <p>
                                    <h5>Rich Text</h5>

                                    <p>Most commonly used </p>

                                    <WorkingButton
                                        variant="primary"
                                        type="button"
                                        size="sm"
                                        text="Copy to clipboard"
                                        workingText="...copying"
                                        delay={0}
                                        onWorkerComplete={this.stopCopy}
                                        onClick={this.copyRichText}
                                    />
                                </p>
                            </Col>
                            <Col sm={12} md={6} lg={6}>
                                <h5>HTML</h5>

                                <p>less common</p>

                                <WorkingButton
                                    variant="secondary"
                                    type="button"
                                    size="sm"
                                    delay={0}
                                    text="Copy to clipboard"
                                    workingText="...copying"
                                    onWorkerComplete={this.stopCopy}
                                    onClick={this.copyHTML}
                                />

                                <div
                                    id="htmlDiv"
                                    style={{
                                        height: 1,
                                        width: 1,
                                        overflow: "hidden",
                                        position: "absolute",
                                    }}
                                >
                                    {ReactDOMServer.renderToStaticMarkup(
                                        <SignatureHTML
                                            isHtml={true}
                                            fullName={
                                                this.state.signatureProps
                                                    ?.fullName || ""
                                            }
                                            email={
                                                this.state.signatureProps
                                                    ?.email || ""
                                            }
                                            pronouns={
                                                this.state.signatureProps
                                                    ?.pronouns || ""
                                            }
                                            workPhone={
                                                this.state.signatureProps
                                                    ?.workPhone || ""
                                            }
                                            cellPhone={
                                                this.state.signatureProps
                                                    ?.cellPhone || ""
                                            }
                                            jobTitle={
                                                this.state.signatureProps
                                                    ?.jobTitle || ""
                                            }
                                            org={
                                                this.state.signatureProps
                                                    ?.org || ""
                                            }
                                            location={
                                                this.state.signatureProps
                                                    ?.location || ""
                                            }
                                            isValid={
                                                this.state.signatureProps
                                                    ?.isValid || true
                                            }
                                        />
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {this.state.isCoppied && (
                                    <Alert
                                        variant="info"
                                        style={{ marginTop: 8 }}
                                    >
                                        {this.getCopyCompleteHtml()}
                                    </Alert>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col
                                style={{
                                    fontSize: "90%",
                                    marginTop: 20,
                                }}
                            >
                                <h6>Outlook How To's</h6>
                                <p>
                                    For other email applications, refer to the
                                    applications help section for more help
                                </p>
                                <ListGroup>
                                    <ListGroupItem
                                        variant="light"
                                        action
                                        target="_blank"
                                        href="https://support.office.com/en-us/article/create-and-insert-a-signature-in-outlook-for-mac-f4d21492-0956-4429-95ad-2769745b539c"
                                    >
                                        How to add a signature in Outlook for
                                        Mac
                                        <FaExternalLinkAlt
                                            size={10}
                                            style={{ marginLeft: 3 }}
                                        />
                                    </ListGroupItem>
                                    <ListGroupItem
                                        action
                                        variant="light"
                                        target="_blank"
                                        href="https://support.office.com/en-us/article/create-and-add-an-email-signature-in-outlook-com-776d9006-abdf-444e-b5b7-a61821dff034"
                                    >
                                        How to add an email signature in
                                        Outlook.com{" "}
                                        <FaExternalLinkAlt
                                            size={13}
                                            style={{ marginLeft: 3 }}
                                        />
                                    </ListGroupItem>
                                </ListGroup>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleClose}>
                        Done
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export { SignatureDialog as Dialog };
